import { LoginErrorCode } from '@exam-room/graphql';
import { Trans, useTranslation } from '@exam-room/translations';
import { useEffect, useState } from 'react';
import { type SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Alert, Card, LanguageSwitchMenu, Page } from '../../components';
import { NetworkAlert } from '../../containers';
import { AuthState, useAuth } from '../../providers';
import { PATHS } from '../constants';

import { LoginForm } from './components';
import { type LoginFormInput } from './components/login-form/schema';

function Login() {
  const [errorMessageKey, setErrorMessageKey] = useState<string>('');

  const { authState, login, isLoading } = useAuth();

  const navigate = useNavigate();
  const { i18n, t } = useTranslation();
  const { language, changeLanguage } = i18n;

  useEffect(() => {
    if (authState === AuthState.LOADING) return;
    if (authState === AuthState.LOGGED_IN) {
      navigate(PATHS.Exam.Index);
    }
  }, [authState]);

  const handleSubmit: SubmitHandler<LoginFormInput> = async (data: LoginFormInput) => {
    setErrorMessageKey('');

    const username = (
      data.idCardNumber +
      data.examCenterCode +
      data.examYearMonth +
      data.examDate +
      data.roundNumber
    )
      .replace(/[^a-zA-Z0-9]/g, '')
      .trim()
      .toLowerCase();

    const password = data.password.trim();

    const result = await login({ username, password });

    if (!result) {
      setErrorMessageKey('login.alert.systemError');
      return;
    }

    if (!result.success) {
      switch (result.errorCode) {
        case LoginErrorCode.UsernameOrPasswordIncorrect:
          setErrorMessageKey('login.alert.usernameOrPasswordIncorrect');
          break;
        case LoginErrorCode.UserBlocked:
          setErrorMessageKey('login.alert.userBlocked');
          break;
        case LoginErrorCode.UserAbsent:
          setErrorMessageKey('login.alert.userAbsent');
          break;
        case LoginErrorCode.UserNotRegistered:
          setErrorMessageKey('login.alert.userNotRegistered');
          break;
        case LoginErrorCode.ExamNotReady:
          setErrorMessageKey('login.alert.examNotReady');
          break;
        case LoginErrorCode.ExamFinished:
          navigate(PATHS.ExamResult, { state: { examineeInfo: result.examineeInfo } });
          break;
        case LoginErrorCode.UserNotEnabled:
          setErrorMessageKey('login.alert.userNotEnabled');
          break;
        case LoginErrorCode.UserNotActive:
          setErrorMessageKey('login.alert.userNotActive');
          break;
        case LoginErrorCode.SystemError:
          setErrorMessageKey('login.alert.systemError');
          break;
        default:
          setErrorMessageKey('login.alert.systemError');
      }
    }
  };

  return (
    <>
      <div className='fixed top-0 w-full p-4'>
        <NetworkAlert />
      </div>
      <div className='bg-grey-50 grid size-full place-items-center'>
        <Card className='h-fit w-[800px] px-10 py-20'>
          <div className='absolute top-6 right-6'>
            <LanguageSwitchMenu
              currentLanguage={language.toUpperCase()}
              onSwitchToEnglish={() => {
                changeLanguage('en');
              }}
              onSwitchToThai={() => {
                changeLanguage('th');
              }}
            />
          </div>
          <div className='mx-auto flex w-[420px] flex-col gap-6'>
            <img src='/logo-white.svg' alt='SET' className='h-20' />
            <p className='text-body1 text-center !font-semibold'>
              <Trans i18nKey='login.title' components={[<p key='login-title' />]} />
            </p>

            {errorMessageKey ? (
              <Alert className='whitespace-pre-line' variant='error' message={t(errorMessageKey)} />
            ) : null}
            <LoginForm onSubmit={handleSubmit} isLoading={isLoading} />
          </div>
        </Card>
      </div>
    </>
  );
}

const LoginRoute = {
  path: PATHS.Login,
  element: (
    <Page>
      <Login />
    </Page>
  ),
};

export default LoginRoute;
